import { useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { v5 } from "uuid";

import { analyticsDashboardApi } from "src/api/endpoints";
import { useCurrentWorkspace } from "src/api/queries";
import type {
  DashboardAttributes,
  DashboardListItemAttributes,
  DashboardPatchAttributes,
  Realm,
} from "src/clients/analytics-dashboard";

const DASHBOARD_NAMESPACE = "6f0d9c4e-2a7b-4d1e-9c3a-000000000001";

export const defaultDashboardId = (realm: Realm, realmId: string): string =>
  v5(`${realm}:${realmId}`, DASHBOARD_NAMESPACE);

const UNCREATED_VERSION = 0;

const PREFIX_CACHE_KEY = "analytics-dashboard";

export const dashboardKeys = {
  all: (wsId: string) => [PREFIX_CACHE_KEY, wsId] as const,
  list: (wsId: string, realm: Realm, realmId: string) =>
    [...dashboardKeys.all(wsId), "list", realm, realmId] as const,
  dashboard: (
    wsId: string,
    realm: Realm,
    realmId: string,
    dashboardId?: string,
  ) => [...dashboardKeys.all(wsId), realm, realmId, dashboardId] as const,
};

const useAnalyticsDashboardClient = () => {
  const { data: workspace } = useCurrentWorkspace();
  const client = useMemo(
    () =>
      workspace?.base_url
        ? analyticsDashboardApi(workspace.base_url)
        : undefined,
    [workspace?.base_url],
  );
  return { client, workspace };
};

export type DashboardListItem = DashboardListItemAttributes & { id: string };

export const useListDashboardsQuery = (realm: Realm, realmId: string) => {
  const { client, workspace } = useAnalyticsDashboardClient();
  const wsId = workspace?.id ?? "";

  return useQuery({
    queryKey: dashboardKeys.list(wsId, realm, realmId),
    queryFn: async (): Promise<DashboardListItem[]> => {
      if (!client) throw new Error("Analytics dashboard client not available");
      const { data } =
        await client.listDashboardsDashboardsApiV1RealmRealmIdDashboardsGet({
          realm,
          realmId,
        });
      return data.data.map((item) => ({
        ...item.attributes,
        id: item.id as unknown as string,
      }));
    },
    enabled: !!client && !!realmId,
    staleTime: 1000 * 30,
  });
};

export const useDashboardQuery = (
  realm: Realm,
  realmId: string,
  dashboardId?: string,
  options?: { enabled?: boolean },
) => {
  const { client, workspace } = useAnalyticsDashboardClient();
  const wsId = workspace?.id ?? "";
  const resolvedId = dashboardId ?? defaultDashboardId(realm, realmId);

  return useQuery({
    queryKey: dashboardKeys.dashboard(wsId, realm, realmId, resolvedId),
    queryFn: async (): Promise<DashboardAttributes> => {
      if (!client) throw new Error("Analytics dashboard client not available");
      try {
        const { data } =
          await client.getDashboardDashboardsApiV1RealmRealmIdDashboardsDashboardIdGet(
            { realm, realmId, dashboardId: resolvedId },
          );
        return data.data.attributes;
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          return { pins: {}, version: UNCREATED_VERSION, members: [] };
        }
        throw error;
      }
    },
    enabled: !!client && !!realmId && (options?.enabled ?? true),
    // Dodges the post-pin remount/PATCH race without leaving other tabs stale.
    staleTime: 1000 * 30,
  });
};

// Derives the next dashboard from the current cached one. Passing an updater
// (rather than a prebuilt snapshot) lets serialized commits compose against the
// latest state, so a queued write can't drop pins an earlier one persisted.
export type DashboardUpdate = (
  current: DashboardAttributes,
) => DashboardAttributes;

export const useUpdateDashboard = (
  realm: Realm,
  realmId: string,
  dashboardId?: string,
) => {
  const { client, workspace } = useAnalyticsDashboardClient();
  const wsId = workspace?.id ?? "";
  const resolvedId = dashboardId ?? defaultDashboardId(realm, realmId);
  const queryClient = useQueryClient();
  const queryKey = dashboardKeys.dashboard(wsId, realm, realmId, resolvedId);

  const mutation = useMutation({
    // Serialize PATCHes per dashboard so concurrent commits can't race.
    scope: {
      id: `${PREFIX_CACHE_KEY}:${wsId}:${realm}:${realmId}:${resolvedId}`,
    },
    mutationFn: async (
      update: DashboardUpdate,
    ): Promise<DashboardAttributes> => {
      if (!client) throw new Error("Analytics dashboard client not available");
      const current = queryClient.getQueryData<DashboardAttributes>(queryKey);
      if (!current) throw new Error("Dashboard not loaded");
      const next = update(current);
      const isCreate = current.version === UNCREATED_VERSION;
      const { data } =
        await client.patchDashboardDashboardsApiV1RealmRealmIdDashboardsDashboardIdPatch(
          {
            realm,
            realmId,
            dashboardId: resolvedId,
            ifMatch: isCreate ? undefined : `"${current.version}"`,
            dashboardPatchRequest: {
              data: {
                type: "dashboards",
                id: resolvedId,
                attributes: {
                  pins: next.pins,
                  members: next.members,
                  name: next.name,
                  icon: next.icon,
                },
              },
            },
          },
          isCreate ? { headers: { "if-none-match": "*" } } : undefined,
        );
      return data.data.attributes;
    },
    onSuccess: (result) => {
      queryClient.setQueryData(queryKey, result);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: dashboardKeys.list(wsId, realm, realmId),
      });
    },
  });

  // Optimistic write; on failure the onSettled refetch reconciles (no rollback).
  const applyOptimistic = (update: DashboardUpdate): void => {
    void queryClient.cancelQueries({ queryKey }, { revert: false });
    queryClient.setQueryData<DashboardAttributes>(queryKey, (current) =>
      current ? update(current) : current,
    );
  };

  // Fire-and-forget for pin/unpin.
  const commit = (update: DashboardUpdate): void => {
    applyOptimistic(update);
    mutation.mutate(update);
  };

  // Awaitable so the layout save can keep edit mode open until the PATCH settles.
  const commitAsync = (
    update: DashboardUpdate,
  ): Promise<DashboardAttributes> => {
    applyOptimistic(update);
    return mutation.mutateAsync(update);
  };

  return { commit, commitAsync, isPending: mutation.isPending };
};

export const useDeleteDashboard = (realm: Realm, realmId: string) => {
  const { client, workspace } = useAnalyticsDashboardClient();
  const wsId = workspace?.id ?? "";
  const queryClient = useQueryClient();

  const listKey = dashboardKeys.list(wsId, realm, realmId);

  return useMutation({
    mutationFn: async (dashboardId: string): Promise<void> => {
      if (!client) throw new Error("Analytics dashboard client not available");
      const { version } = await queryClient.ensureQueryData({
        queryKey: dashboardKeys.dashboard(wsId, realm, realmId, dashboardId),
        queryFn: async () => {
          const { data } =
            await client.getDashboardDashboardsApiV1RealmRealmIdDashboardsDashboardIdGet(
              { realm, realmId, dashboardId },
            );
          return data.data.attributes;
        },
      });
      await client.deleteDashboardDashboardsApiV1RealmRealmIdDashboardsDashboardIdDelete(
        { realm, realmId, dashboardId, ifMatch: `"${version}"` },
      );
    },
    onMutate: async (dashboardId) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<DashboardListItem[]>(listKey);
      queryClient.setQueryData<DashboardListItem[]>(listKey, (old) =>
        (old ?? []).filter((d) => d.id !== dashboardId),
      );
      return { previous };
    },
    onError: (_error, _dashboardId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(listKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
};

export const useCreateDashboard = (realm: Realm, realmId: string) => {
  const { client, workspace } = useAnalyticsDashboardClient();
  const wsId = workspace?.id ?? "";
  const queryClient = useQueryClient();

  const listKey = dashboardKeys.list(wsId, realm, realmId);

  return useMutation({
    mutationFn: async (
      args: { dashboardId: string } & Partial<DashboardPatchAttributes>,
    ): Promise<DashboardAttributes> => {
      if (!client) throw new Error("Analytics dashboard client not available");
      const { data } =
        await client.patchDashboardDashboardsApiV1RealmRealmIdDashboardsDashboardIdPatch(
          {
            realm,
            realmId,
            dashboardId: args.dashboardId,
            dashboardPatchRequest: {
              data: {
                type: "dashboards",
                id: args.dashboardId,
                attributes: {
                  pins: args.pins ?? {},
                  members: args.members,
                  name: args.name,
                  icon: args.icon,
                },
              },
            },
          },
          { headers: { "if-none-match": "*" } },
        );
      return data.data.attributes;
    },
    onMutate: async (args) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<DashboardListItem[]>(listKey);
      queryClient.setQueryData<DashboardListItem[]>(listKey, (old) => [
        ...(old ?? []),
        {
          id: args.dashboardId,
          members: args.members ?? [],
          name: args.name ?? null,
          icon: args.icon ?? null,
        },
      ]);
      return { previous };
    },
    onError: (_error, _args, context) => {
      if (context?.previous) {
        queryClient.setQueryData(listKey, context.previous);
      }
    },
    onSuccess: (data, args) => {
      queryClient.setQueryData(
        dashboardKeys.dashboard(wsId, realm, realmId, args.dashboardId),
        data,
      );
      queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
};
